import * as Sentry from '@sentry/browser';

import { isMac, isWindows } from './index';
import { INTERNAL_AUDIO_MIC_ID, isInternalAudio } from './internalAudio';
import { getSocketIfExist } from './getSocket';
import { sendAudioParams, sendMbackendMessage } from './ws-v1';

import { createAudioProcessor, getFormat } from '../modules/AudioRecorder/audioProcessing';
import {
  AUDIO_CONSTRAINTS,
  clearVolumeInterval,
  closeAudioContext,
  disconnectSources,
  getCurrentAvailableMics,
  getMicrophoneByIdIfExist,
  getWindowsAudioStream,
  isBluetoothMic,
  stopAudioProcessor,
  stopStreams,
  trackMicPermissionGrantedIfPromptExists,
} from '../helpers/audioRecorder';
import LocalStorage, { STORAGE_KEYS } from '../services/localStorage';

// Microphone is selected in the following priority:
//  1. Selected mic from Redux state
//  2. Saved mic from local storage if user manually selects a mic
//  3. Ava mic if available (Desktop app or Windows browser (except Firefox))
//  4. Default mic, Google Chrome will label this while other browsers might not
//  5. First available mic
export const getMicIdToSelect = (availableMics, selectedMicId) => {
  if (!availableMics.length || availableMics[0].value === 'Missing') {
    return null;
  }

  let savedMicId = LocalStorage.get(STORAGE_KEYS.SELECTED_MIC_ID);

  const selectedMic = getMicrophoneByIdIfExist(selectedMicId);
  const savedMic = getMicrophoneByIdIfExist(availableMics, savedMicId);
  const avaMic = availableMics.find(mic => isInternalAudio(mic));
  const defaultMic = availableMics.find(mic => mic.default);
  const firstAvailableMic = availableMics[0];

  if (!selectedMic || selectedMicId !== savedMicId) {
    const { value } = selectedMic || savedMic || avaMic || defaultMic || firstAvailableMic;
    return value;
  }

  return selectedMicId;
};

export const getMediaDevices = async () => {
  // Note that the following doesn't get full device info (enumerates only default devices and
  // without labels or ids) until the user grants permission - a manual process which
  // is triggered as part of recording with `navigator.mediaDevices.getUserMedia()` and
  // `navigator.mediaDevices.getDisplayMedia()` (or by the user changing browser settings,
  // which needs to happen if they denied access at one point. We nudge them in the right
  // direction with <MicrophoneDenied /> modal).
  return await navigator.mediaDevices.enumerateDevices();
};

export const getAvailableMicsFromMediaDevices = (devices, properties) => {
  const { isMuted, participants, t } = properties;

  const isAvaMicForbidden = (participants.length > 1 && !isMuted) || (isMac && !window.isElectron);
  const availableMics = getCurrentAvailableMics(devices, isAvaMicForbidden, t);

  return availableMics;
};

const initAudioStreams = async (availableMics, micIdSelected, onlyInternalAudio, setIsPermissionGranted) => {
  const { mediaDevices } = window.navigator;
  const streams = [];
  const streamNames = [];
  const mic = availableMics.find(m => m.value === micIdSelected);

  // First time users will be asked to grant access to microphone
  // (via mediaDevices.getUserMedia or mediaDevices.getDisplayMedia).
  // If they don't grant it, then an exception will be thrown
  // ('Permission denied' or 'Permission dismissed' DOMException).
  if (mic) {
    let audioStream;
    let listeningToInternalAudio = false;

    if (INTERNAL_AUDIO_MIC_ID === mic.value) {
      // This part is only relevant for Windows, because only in Windows a browser can record
      // system sound/speakers.
      audioStream = await getWindowsAudioStream();
      listeningToInternalAudio = true;
    } else {
      const deviceId = !isWindows && mic.value ? { exact: mic.value } : { ideal: 'default' };

      audioStream = await mediaDevices.getUserMedia({
        audio: { ...AUDIO_CONSTRAINTS, deviceId },
      });

      if (isInternalAudio(mic)) {
        listeningToInternalAudio = true;
      }
    }

    streams.push(audioStream);
    streamNames.push(mic.label);

    // If using internal audio, we need to check that there is at least one
    // available microphone.
    if (listeningToInternalAudio && !onlyInternalAudio && availableMics.length > 1) {
      // There is internal audio, adding a second stream for mic
      try {
        const secondAudioStream = await mediaDevices.getUserMedia({
          audio: { ...AUDIO_CONSTRAINTS, deviceId: 'default' },
        });

        if (!streams.find(stream => stream && stream.id === secondAudioStream.id)) {
          streams.push(secondAudioStream);
          const defaultMic = availableMics.find(mic => mic.default === true);
          if (defaultMic) {
            streamNames.push(defaultMic.label);
          }
        }
      } catch (e) {
        // Getting the other stream is nice to have, but not a blocker to initiate
        // recording, so any errors during the second stream capture are ignored.
        Sentry.captureException(e);
      }
    }

    if (audioStream.getAudioTracks().length > 0) {
      setIsPermissionGranted(true);
    }
  }

  return { streams, streamNames };
};

const setupVolumeMetering = async (audioContext, setVolume) => {
  // Volume metering is needed to display volume levels
  // to the user (shadow on the mic button).
  //
  // Because it is loaded as a separate script/file, it can happen
  // that audioContext fails to fetch it. For example, if recording
  // is requested in offline mode, the vumeter-processor.js will
  // fail to load in case it hasn't been cached yet.
  //
  // To avoid showing the user red error banner in such cases,
  // we wrap this setup in a try .. catch block.
  let volumeMeterWorkletNode;
  let volumeInterval;

  try {
    volumeMeterWorkletNode = new AudioWorkletNode(audioContext, 'vumeter');

    const volumeHandler = () => {
      let volumeAverage = 0;
      let volumeCount = 0;
      let prevVolumeAverage = 0;

      volumeInterval = setInterval(() => {
        if (Math.abs(prevVolumeAverage - volumeAverage) > 1) {
          setVolume(volumeAverage);
          prevVolumeAverage = volumeAverage;
        }

        volumeCount = 0;
        volumeAverage = 0;
      }, 250);

      return event => {
        const eventVolume = event.data.volume || 0;
        const newVolumeSum = volumeAverage * volumeCount + eventVolume * 100;
        volumeCount += 1;
        volumeAverage = newVolumeSum / volumeCount;
      };
    };

    volumeMeterWorkletNode.port.onmessage = volumeHandler();
  } catch (err) {
    Sentry.captureException(err);
  } finally {
    return { volumeInterval, volumeMeterWorkletNode };
  }
};

const notifyAudioParamsUpdated = (options, format, selectedMic, recordingStart) => {
  const { curseFilter, lang, setAudioParamsSent, speechLang } = options;
  const mic = isBluetoothMic(selectedMic) ? 'BT' : 'BUILTIN';
  const translation = lang && lang !== '~' ? { target: lang } : undefined;

  const socket = getSocketIfExist();
  if (socket) {
    const updatedAudio = {
      lang: speechLang,
      pFilter: curseFilter,
      mic,
      format,
      sampleRateHz: 16000,
      recordingMode: 'web',
      chunkLengthMs: 60,
      recordingStart,
      translation,
    };

    sendAudioParams(socket, updatedAudio);
    setAudioParamsSent(true);

    return { socketUrl: socket.url, sent: true };
  }

  return { socketUrl: null, sent: false };
};

const createDataSender = onFirstPacket => {
  let buffer = [];
  let firstPacketSent = false;
  let firstPacketUrl = null;

  return data => {
    const packet = { ...data, type: 'write' };

    const socket = getSocketIfExist();
    if (socket && socket.readyState === WebSocket.OPEN) {
      // Check that the first packet (connection params) has been sent and
      // that it's the same socket url. Url to the backend contains unique token,
      // so a different socket will have different url.
      if (firstPacketSent && firstPacketUrl === socket.url) {
        // empty the buffer, if it's been accumulated for some reason
        // (websocket down/reconnecting).
        while (buffer.length > 0) {
          sendMbackendMessage(socket, buffer.shift());
        }
        sendMbackendMessage(socket, packet);
      } else {
        const { socketUrl, sent } = onFirstPacket();
        firstPacketSent = sent;
        firstPacketUrl = socketUrl;
        buffer.push(packet);
      }
    } else {
      firstPacketSent = false;
      firstPacketUrl = null;
      buffer.push(packet);
      if (buffer.length > 100) {
        // If 6s pass we throw away the packets, because we can't
        // hold them in the buffer forever.
        buffer = [];
      }
    }
  };
};

export const setupRecording = async (properties, handleMicrophoneError) => {
  const {
    availableMics,
    audioParamsSent,
    micIdSelected,
    onlyInternalAudio,
    setAudioParamsSent,
    setIsPermissionGranted,
    setVolume,
    trackMicPermissionPrompt,
  } = properties;

  if (audioParamsSent) {
    setAudioParamsSent(false);
  }

  try {
    const audioContext = new AudioContext();
    // By giving direct url of the worklet module it is less error prone
    // (in case of double slash or (not) using hash in routing).
    const moduleUrl = new URL('/vumeter-processor.js', window.location.href);
    await audioContext.audioWorklet.addModule(moduleUrl);

    const { streams, streamNames } = await initAudioStreams(
      availableMics,
      micIdSelected,
      onlyInternalAudio,
      setIsPermissionGranted
    );
    trackMicPermissionGrantedIfPromptExists(trackMicPermissionPrompt);
    const { volumeInterval, volumeMeterWorkletNode } = await setupVolumeMetering(audioContext, setVolume);

    const sources = streams.map(stream => {
      const mediaStream = audioContext.createMediaStreamSource(stream);
      if (volumeMeterWorkletNode) {
        mediaStream.connect(volumeMeterWorkletNode);
      }
      return mediaStream;
    });

    const recordingStart = Date.now();
    const format = getFormat();
    const mic = availableMics.find(m => m.value === micIdSelected);
    const notifyAudioParamsUpdatedOnRecordingStart = () => {
      return notifyAudioParamsUpdated(properties, format, mic, recordingStart);
    };

    let audioProcessor = null;
    if (sources.length && streams.length && streamNames.length) {
      audioProcessor = createAudioProcessor({
        audioContext,
        sources,
        streams,
        streamNames,
        onData: createDataSender(notifyAudioParamsUpdatedOnRecordingStart),
      });
    }

    return {
      streams,
      streamNames,
      sources,
      audioProcessor,
      audioContext,
      volumeInterval,
    };
  } catch (err) {
    handleMicrophoneError(err);
  }
};

export const startRecording = async (properties, setRecordingContext, handleMicrophoneError) => {
  const recordingContext = await setupRecording(properties, handleMicrophoneError);
  setRecordingContext(recordingContext);
};

export const stopRecording = resetRecordingContext => {
  resetRecordingContext();
};

export const destroyRecordingContext = async recordingContext => {
  if (!recordingContext) return;

  const { audioContext, audioProcessor, sources, streams, volumeInterval } = recordingContext;
  stopAudioProcessor(audioProcessor);
  stopStreams(streams);
  disconnectSources(sources);
  await closeAudioContext(audioContext);
  clearVolumeInterval(volumeInterval);
};
